/**
 * The async boot -- the heavy assets, the net, and everything server-driven,
 * loaded in the order the player feels them and written into the context as
 * each one lands.
 *
 * This was main.js's boot IIFE, and its timing is load-bearing: the frame
 * loop draws from the first rAF, gated only by game.ready, so everything
 * here happens behind a live title card.  The assets arrive here too, by id
 * through the one loader (../assets/load.js, docs/ARCH-PLAN.md §3.1) -- no
 * module anywhere imports an asset file, which is why tests, tools and the
 * soak all run without the generated asset pipeline, and why this file
 * cannot know whether the bytes came from a hashed URL or a data: URI.
 */
import { Cat } from "../cat.js";
import { buildCar } from "../car.js";
import { CAR } from "@catworld/engine";
import { Customizer } from "../customizer/index.js";
import { Net, localToken, saveToken } from "../net/index.js";
import { HappyHud } from "../happyhud.js";
import { Purr } from "../purr.js";
import { Butterflies } from "../butterflies.js";
import { Prey } from "../prey.js";
import { Finds } from "../finds.js";
import { achieveFound, achieveHunt, achievePlace, achieveRoster } from "../achievements.js";
import { Dog } from "../dog.js";
import { Join } from "../join.js";
import { PLACE_BY_ID } from "@catworld/shared";
import { game, showCatName, switchCat } from "./context.js";
// The page handles, from the modules that made the elements (ui/index.js
// holds the order).  Boot wires subsystems to them; it never looks up an id.
import { ui } from "../ui/index.js";
import { loadBytes, loadJson } from "../assets/load.js";

// Ids, not paths: build.mjs's catalog is the other spelling of this list,
// and an id it does not know fails the first load naming the catalog.  The
// order is the on-screen order -- the array is walked one cat at a time
// below, and cat 0 is the one visible when the title card lifts.
const CATS = ["sandy", "ginger", "gray", "toon", "toon-tabby"];

export async function boot() {
  await game.loadExterior();
  // The car first, still -- it is one object on a driveway the player can
  // see from the spawn -- but it no longer awaits anything: since the
  // vendored model went, the car is built synchronously from the engine's
  // own tier data (car.js), so it exists before the first cat's bytes are
  // even asked for.  The awaited loads below stay sequential on purpose --
  // the awaited order IS the loading order the contract pins, and the
  // artifact face resolves from data: URIs where parallelism buys nothing.
  game.scene.add(buildCar(CAR));

  for (const key of CATS) {
    const cat = await Cat.load(await loadBytes(`cat/${key}`),
                               await loadJson(`cat/${key}.clips`));
    cat.root.visible = false;
    game.scene.add(cat.root);
    game.cats.push(cat);
  }
  game.cats[game.active].root.visible = true;

  // Net first, and specifically before the Customizer.
  //
  // roster.js captures a pristine template per base at construction, and
  // scrubs the bones back to the base morph's captured rest so a cat the
  // player has already dressed cannot leak its proportions into a stranger's.
  // The bones it can fix; a flat iris COLOUR is not a bone -- on the toon
  // rigs it is written straight onto the material, and a look restored from
  // the last session applies during `customizer.start()`.  Built after that,
  // the templates would carry this player's eye tint into every default-look
  // toon on the street.  Constructed here, they are taken off cats nobody has
  // touched yet, and the question does not arise.
  game.net = new Net({
    bases: game.cats,
    scene: game.scene,
    camera: game.camera,
    callbacks: {
      onClaim: ({ token }) => {
        // The one secret the system has, and the only thing that makes this
        // cat ours tomorrow.  Stored before anything else touches the socket.
        saveToken(token);
        game.join?.hide();
        game.startPlaying();
        game.seatWanted = true;      // fetch the cat from wherever it wandered to
        game.happyHud?.show();
      },
      onRoster: (list) => {
        game.catNames.clear();
        let humans = 0;
        for (const c of list) {
          game.catNames.set(c.slot, c.name);
          if (c.human) humans++;
          // Our own row is the server's word on what this cat is called, and
          // it is the only place that word exists.  A name typed into the join
          // card goes over the wire and is cleaned, capped and de-duplicated
          // there; the customizer never sees it, so without this the HUD
          // introduces the cat you just named Thumbelina as "Grey toon tabby".
          // Comparing first because setLook re-applies the morph and the pelt,
          // and a roster message arrives every time anyone joins or renames.
          if (game.net && c.slot === game.net.slot && game.customizer &&
              c.name && game.customizer.displayName("") !== c.name) {
            game.customizer.setLook({ ...game.customizer.look, name: c.name });
          }
        }
        game.happyHud?.setRoster(list.length, humans);
        achieveRoster(list.length);   // §4.4 FULL_HOUSE -- a full street, human or AI
      },
      onNeeds: (wants, progress) => game.happyHud?.setNeeds(wants, progress),
      onBliss: (slot, placeId) => game.happyHud?.bliss(placeId, game.catNames.get(slot)),
      onStatus: /** @param {{ denied?: string | null, deniedOpen?: boolean, offline?: boolean }} s */
                ({ denied, deniedOpen, offline }) => {
        // Offline is not a failure to report: it is how the game has always
        // run off a file, and a red banner over a working game is a bug.
        //
        // The join card has to go with it, though.  It asks to be admitted
        // to a roster; offline there is no roster, nothing will ever answer,
        // and `commit` hands its name to a net layer that no-ops -- so left
        // up it is a modal gate on a game that is already running behind it.
        // Nobody saw this on file://, where connect() decides offline before
        // the card is ever asked for, or in production, where the server
        // answers; it is every host in between that got stuck.
        if (offline) { game.happyHud?.hide(); game.join?.hide(); return; }
        if (denied && denied !== "proto") game.join?.showDenied(denied, { open: deniedOpen });
      },
    },
  });

  game.customizer = new Customizer({
    cats: game.cats,
    mount: ui.customizerMount,
    setBase: switchCat,
  });
  game.customizer.onName = showCatName;
  game.customizer.start(game.active);
  ui.openCustomizer.addEventListener("click", (e) => {
    e.stopPropagation();      // the canvas takes a click as "start playing"
    game.customizer.toggle();
  });
  showCatName();
  // Was hardcoded to cat 1 back when that was the starting cat; with the
  // default moved it would have highlighted Ginger while you played Tabby.
  ui.catButtons.find((el) => Number(el.dataset.cat) === game.active)?.classList.add("on");
  ui.loading.classList.add("hidden");
  game.ready = true;

  // ---- the shared world ----------------------------------------------------
  // Everything above this line is the single-player game and still works with
  // no server at all: net.connect() gives up quietly on file:// and the panels
  // below simply never fill.  That is not politeness -- game/dist/artifact.html
  // is published with no server behind it, and it has to stay a game.
  game.happyHud = new HappyHud(ui.happy, ui.bliss);
  game.happyHud.hide();      // nothing to say until a cat has been claimed
  game.purr = new Purr();
  game.purr.bindMute(ui.mute);
  game.butterflies = new Butterflies(PLACE_BY_ID.butterflies);
  game.scene.add(game.butterflies.object3D);

  // ---- the hunt: mice and birds (packages/client/src/prey.js) -------------------------
  // Server-authoritative end to end: positions, captures and deliveries all
  // arrive on the wire, so with no server there are simply no prey -- the
  // butterflies stay the offline garden's life.  This block draws the rows,
  // glues carried catches to their cats, and relays the two hunt moments to
  // the HUD.  Handlers are registered straight on the client's handler table
  // (it keeps a Set per tag) rather than through Net's callback object, so
  // the hunt's wiring lives here in one piece.
  game.prey = new Prey();
  game.scene.add(game.prey.object3D);
  game.prey.bindPoses({
    // Our own catch rides the LOCAL player, which is drawn frames ahead of
    // its own snapshot; everyone else's rides the same sampleLive their cat
    // is drawn from.  Mixing those up trails a mouse behind our own mouth.
    ownSlot: () => (game.net ? game.net.slot : -1),
    ownPose: () => ({ x: game.player.pos.x, y: game.player.pos.y, z: game.player.pos.z,
                      facing: game.player.facing }),
    sample: (slot, out) => game.net?.client.sampleLive(slot, out) ?? null,
  });
  game.net.client.on("prey", (msg) => {
    if (!Array.isArray(msg.p)) return;
    game.prey.setRows(msg.p);
    // The "carry it home -- worth double" line follows what the server says
    // is in our mouth, so it can never promise a bonus the server will not pay.
    game.happyHud?.setCarry(game.net.slot >= 0 ? game.prey.carriedBy(game.net.slot) : null);
  });
  game.net.client.on("hunt", (msg) =>
    game.happyHud?.hunted(msg.kind, msg.species, game.catNames.get(msg.slot),
                          msg.slot === game.net.slot));

  // ---- the §4.4 achievement edges (../achievements.js) --------------------
  // Second handlers on their tags, the tally's trick: the callback blocks
  // above stay untouched, the module owns every condition (threshold,
  // dedupe, the visited-places arithmetic), and these lines only say whose
  // moment it was.  bliss feeds eleven of ALL_PLACES' thirteen; the hunt's
  // delivery and a dug-up treasure are the other two (they never arrive as
  // a bliss -- happiness.js banks them through their own paydays).
  const net = /** @type {InstanceType<typeof Net>} */ (/** @type {unknown} */ (game.net));
  net.client.on("bliss", (/** @type {{ slot: number, place: string }} */ msg) =>
    achievePlace(msg.slot === net.slot, msg.place));
  net.client.on("hunt", (/** @type {{ slot: number, kind: string }} */ msg) =>
    achieveHunt(msg.slot === net.slot, msg.kind));
  net.client.on("found", (/** @type {{ slot: number }} */ msg) =>
    achieveFound(msg.slot === net.slot));

  // ---- the dog (packages/client/src/dog.js) -------------------------------------------
  // Server-authoritative like the prey, only more so: one row per snapshot
  // is the dog's whole existence on this end, so with no server there is no
  // dog and the offline garden stays a garden.  The renderer appears on its
  // first row and animates whatever the row's mode says; nothing else in
  // the client knows the dog exists.
  game.dog = new Dog();
  game.scene.add(game.dog.object3D);
  game.net.client.on("dog", (msg) => game.dog.setRow(msg.d));
  // The collection tally rides the needs message; a second handler on the
  // same tag keeps the Net constructor's callback block untouched.
  game.net.client.on("needs", (msg) => game.happyHud?.setTally(msg.tal));

  // ---- the hidden treasures (packages/client/src/finds.js) ----------------------------
  // Server-authoritative like the hunt: which treasures OUR cat can find
  // arrives on the owner-only `finds` message, discoveries arrive on `found`,
  // and with no server nothing is drawn (the prey bargain).  The renderer is
  // self-driven off its own onBeforeRender, so this block is the feature's
  // entire footprint in this file: no render-loop call, no top-level
  // variable -- the handlers below are the only things that hold it.
  const finds = new Finds({
    // The LOCAL player, frames ahead of its own snapshot, for the same
    // reason prey.bindPoses takes ownPose: the glint's "you're on it" cue
    // must agree with the paws on screen, not with the wire.
    playerPos: () => game.player.pos,
  });
  game.scene.add(finds.object3D);
  game.net.client.on("finds", (msg) => {
    finds.setState(msg.f);
    game.happyHud?.setFinds(msg.f);
  });
  game.net.client.on("found", (msg) =>
    game.happyHud?.found(msg.id, game.catNames.get(msg.slot), msg.slot === game.net.slot,
                         msg.first === true));

  // ---- the time of day (packages/client/src/daycycle.js) ------------------------------
  // Server-owned like the prey: everyone on the street shares one sky, so
  // the client renders whatever phase the server says it is.  One handler is
  // the whole wiring -- daycycle integrates the clock itself between words
  // and only eases out the drift, and with no server at all (file://, the
  // artifact build) it free-runs on the same wall-clock formula the server
  // uses, so offline play still has days.
  game.net.client.on("day", (msg) => game.daycycle.onServerPhase(msg.ph));
  net.client.on("door", (/** @type {{side: string, door?: string}} */ msg) => game.door?.arrive(msg));

  game.join = new Join(ui.join, {
    openCustomizer: ui.openCustomizer,
    onOpen: () => ui.overlay.classList.add("hidden"),
  });

  // Every slider drag and every keystroke in the name field, debounced in
  // net/: the other fifteen people are drawing this cat from a record the
  // server holds, so a look kept to ourselves is a look nobody sees.
  game.customizer.onLook = (look) => game.net.sendLook(look);
  const priorName = game.customizer.displayName("");
  game.customizer.onName = () => { showCatName(); game.net.sendName(game.customizer.displayName("")); };

  game.net.connect();
  const token = localToken();
  if (token) {
    // A returning player never sees the join card: their cat is already on the
    // street and has been getting on with its day.
    game.net.join({ token, name: priorName, look: game.customizer.look });
    game.happyHud.show();
  } else if (!game.net.offline) {
    // First visit.  The card sits over the title and the game is already live
    // behind it, so naming a cat is a thing you do, not a gate you pass.
    game.happyHud.hide();
    const { name, look } = await game.join.ask({ suggestedName: priorName });
    game.net.join({ name, look });
  } else {
    game.happyHud.hide();
  }
}
